import React, { useState } from 'react';
import { User, Lock, Bell, Shield, LogOut, Camera, Edit, Save, X, CreditCard, Crown, Calendar, AlertCircle, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import UpgradeModal from '../components/UpgradeModal';

const Profile = () => {
  const { user, token, logout, uploadProfilePicture } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const isAdmin = user?.is_admin === true;
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [conchiMemory, setConchiMemory] = useState(null);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [blueprint, setBlueprint] = useState(null);
  const [blueprintLoading, setBlueprintLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    gender: user?.gender || '',
    date_of_birth: user?.date_of_birth || ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Load blueprint and subscription on mount (skip for admins)
  React.useEffect(() => {
    if (!isAdmin) {
      loadBlueprint();
      loadSubscription();
    } else {
      setBlueprintLoading(false);
      setSubscriptionLoading(false);
    }
  }, [isAdmin]);

  const loadBlueprint = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/onboarding/blueprint`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_blueprint) {
          setBlueprint(data.blueprint);
        }
      }
    } catch (error) {
      console.error('Error loading blueprint:', error);
    } finally {
      setBlueprintLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsManagingSubscription(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/subscription/create-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: window.location.href
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Customer Portal
        window.location.href = data.portal_url;
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive",
      });
      setIsManagingSubscription(false);
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  // Format date to MM/DD/YYYY with single-digit months
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    // Handle date-only strings (YYYY-MM-DD) by treating them as local dates
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
    }
    
    // For datetime strings, use Date object
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const year = date.getFullYear();
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  };

  // Helper to ensure date is in YYYY-MM-DD format
  const normalizeDateString = (dateString) => {
    if (!dateString) return '';
    
    // If already in YYYY-MM-DD format, return as-is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Otherwise, parse and format
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Different tabs for admin vs regular users
  const tabs = isAdmin ? [
    { id: 'profile', label: 'Profile Info', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
  ] : [
    { id: 'profile', label: 'Profile Info', icon: User },
    { id: 'blueprint', label: 'Personality', icon: User },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Shield }
  ];

  const handleProfileUpdate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        
        // Update AuthContext with new user data
        // We need to manually update the user object in AuthContext
        // Force a context refresh by updating localStorage and reloading user data
        const currentUser = { ...user, ...profileForm };
        
        toast({
          title: "Profile Updated! ✅",
          description: "Your profile information has been saved successfully.",
          duration: 3000,
        });
        setIsEditing(false);
        
        // Reload page to refresh AuthContext (ensures birthday updates everywhere)
        window.location.reload();
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: "Password Error",
        description: "New passwords don't match.",
        variant: "destructive",
      });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast({
        title: "Password Error", 
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    try {
      // TODO: Implement password change API call
      toast({
        title: "Password Changed! ✅",
        description: "Your password has been updated successfully.",
        duration: 3000,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({
        title: "Password Change Failed",
        description: "Failed to update password. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      toast({
        title: "File Too Large",
        description: "Please select an image under 5MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      await uploadProfilePicture(file);
      toast({
        title: "Picture Updated! ✅",
        description: "Your profile picture has been updated.",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to update profile picture.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const loadConchiMemory = async () => {
    setPrivacyLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/privacy/conchi-memory`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConchiMemory(data);
      }
    } catch (error) {
      console.error('Error loading Conchi memory:', error);
    } finally {
      setPrivacyLoading(false);
    }
  };

  const exportUserData = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/privacy/export-data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `conchi_data_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        
        toast({
          title: "Data Exported! 📄",
          description: "Your complete data has been downloaded.",
          duration: 4000,
        });
      }
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deleteConchiMemory = async () => {
    if (!confirm('Are you sure you want to delete all of Conchi\'s memories? This action cannot be undone.')) {
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/privacy/delete-memory`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setConchiMemory(null);
        toast({
          title: "Memory Cleared! 🧠",
          description: "Conchi's memory has been reset. She'll start fresh with you.",
          duration: 4000,
        });
      }
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to clear memory. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Load Conchi memory when privacy tab is selected
  React.useEffect(() => {
    if (activeTab === 'privacy' && !conchiMemory) {
      loadConchiMemory();
    }
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
      duration: 3000,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <User className="w-8 h-8 text-rose-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Profile Settings</h1>
          </div>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Profile Card */}
          <div className="lg:w-1/3">
            <Card className="p-6 text-center bg-white/70 backdrop-blur-sm border-rose-200">
              <div className="relative mb-6">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-rose-400 to-rose-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                  {user?.profile_pic ? (
                    <img src={user.profile_pic} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.charAt(0).toUpperCase()
                  )}
                </div>
                <label className="absolute bottom-0 right-1/2 transform translate-x-8 translate-y-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-2 cursor-pointer transition-colors">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                </label>
                {isUploading && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              
              <h2 className="text-xl font-semibold text-gray-800 mb-2">{user?.name}</h2>
              <p className="text-gray-600 mb-2">{user?.email}</p>
              
              {/* Admin or Founding Member Badge */}
              {user?.is_admin ? (
                <div className="mb-4">
                  <div 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-purple-500 to-pink-600 text-white"
                  >
                    <span className="mr-1">👑</span>
                    Admin
                  </div>
                </div>
              ) : user?.is_founding_member ? (
                <div className="mb-4">
                  <div 
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-serif font-semibold"
                    style={{ 
                      background: 'linear-gradient(45deg, #FFD9E6, #FADADD)',
                      color: '#8B5D3B'
                    }}
                  >
                    <span className="mr-1">🌟</span>
                    Founding Member
                  </div>
                </div>
              ) : null}
              
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Gender:</span> {user?.gender || 'Not set'}</p>
                <p><span className="font-medium">Birth Date:</span> {user?.date_of_birth ? formatDate(user.date_of_birth) : 'Not set'}</p>
                <p><span className="font-medium">Member since:</span> {user?.created_at ? formatDate(user.created_at) : 'Not set'}</p>
              </div>
            </Card>
          </div>

          {/* Settings Tabs */}
          <div className="lg:w-2/3">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6 p-2 bg-white/50 rounded-lg">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-rose-500 text-white'
                        : 'text-gray-600 hover:bg-white hover:text-rose-600'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <Card className="p-6 bg-white/70 backdrop-blur-sm border-rose-200">
              {activeTab === 'profile' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-800">Profile Information</h3>
                    <Button
                      onClick={() => isEditing ? handleProfileUpdate() : setIsEditing(true)}
                      className={isEditing ? "bg-green-500 hover:bg-green-600" : "bg-rose-500 hover:bg-rose-600"}
                    >
                      {isEditing ? <Save className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                      {isEditing ? 'Save Changes' : 'Edit Profile'}
                    </Button>
                    {isEditing && (
                      <Button
                        onClick={() => {
                          setIsEditing(false);
                          setProfileForm({
                            name: user?.name || '',
                            email: user?.email || '',
                            gender: user?.gender || '',
                            date_of_birth: user?.date_of_birth || ''
                          });
                        }}
                        variant="outline"
                        className="ml-2"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                        <select
                          value={profileForm.gender}
                          onChange={(e) => setProfileForm({ ...profileForm, gender: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-gray-100"
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Birth Date</label>
                        <input
                          type="date"
                          value={normalizeDateString(profileForm.date_of_birth)}
                          onChange={(e) => setProfileForm({ ...profileForm, date_of_birth: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'blueprint' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-6">Personality & Blueprint</h3>
                  
                  {blueprintLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading your blueprint...</p>
                    </div>
                  ) : !blueprint ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🌸</div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        No Blueprint Yet
                      </h4>
                      <p className="text-gray-600 mb-6">
                        Complete your personality assessment to discover your archetype and personalized roadmap.
                      </p>
                      <Button
                        onClick={() => navigate('/onboarding')}
                        className="bg-rose-500 hover:bg-rose-600"
                      >
                        Take Assessment
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Archetype Card */}
                      <div className="bg-gradient-to-br from-rose-400 to-rose-600 rounded-3xl p-8 text-white text-center shadow-xl">
                        <div className="text-7xl mb-4">{blueprint.emoji}</div>
                        <h2 className="text-3xl font-bold mb-2">
                          The {blueprint.primary_archetype}
                        </h2>
                        {blueprint.secondary_archetype && (
                          <p className="text-rose-100 mb-4">
                            with {blueprint.secondary_archetype} tendencies
                          </p>
                        )}
                        <p className="text-xl font-semibold text-yellow-200 mb-4">
                          {blueprint.tagline}
                        </p>
                        <p className="text-rose-50 leading-relaxed">
                          {blueprint.description}
                        </p>
                      </div>

                      {/* Current & Future State */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                          <h4 className="font-bold text-blue-900 mb-2">Where You Are Now</h4>
                          <p className="text-sm text-blue-800">{blueprint.current_state}</p>
                        </div>
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                          <h4 className="font-bold text-green-900 mb-2">Where You're Going</h4>
                          <p className="text-sm text-green-800">{blueprint.future_state}</p>
                        </div>
                      </div>

                      {/* Roadmap */}
                      <div className="bg-white border-2 border-rose-100 rounded-lg p-6">
                        <h4 className="text-lg font-bold text-gray-900 mb-4">Your NeuroLove Roadmap</h4>
                        
                        <div className="space-y-4">
                          <div>
                            <h5 className="font-bold text-blue-700 mb-2">🔍 Reflect</h5>
                            <ul className="space-y-1 text-sm text-gray-700">
                              {blueprint.roadmap.reflect.map((item, idx) => (
                                <li key={idx}>• {item}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-bold text-purple-700 mb-2">✨ Reframe</h5>
                            <ul className="space-y-1 text-sm text-gray-700">
                              {blueprint.roadmap.reframe.map((item, idx) => (
                                <li key={idx} className="italic">• "{item}"</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-bold text-green-700 mb-2">🌱 Rewire</h5>
                            <ul className="space-y-1 text-sm text-gray-700">
                              {blueprint.roadmap.rewire.map((item, idx) => (
                                <li key={idx}>• {item}</li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-bold text-orange-700 mb-2">🌸 Reinforce</h5>
                            <p className="text-sm text-gray-700">• {blueprint.roadmap.reinforce}</p>
                          </div>
                        </div>
                      </div>

                      {/* Retake Button */}
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
                        <p className="text-gray-700 mb-4">
                          Your archetype may evolve as you grow. Feel free to retake the assessment anytime to update your blueprint.
                        </p>
                        <Button
                          onClick={() => navigate('/onboarding')}
                          variant="outline"
                          className="border-purple-400 text-purple-700 hover:bg-purple-100"
                        >
                          Retake Assessment
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'subscription' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-6">Subscription & Billing</h3>
                  
                  {subscriptionLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading subscription details...</p>
                    </div>
                  ) : !subscriptionData ? (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Unable to load subscription data</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Current Plan Card */}
                      <div className={`border-2 rounded-lg p-6 ${
                        (subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial')
                          ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-300' 
                          : 'bg-gray-50 border-gray-300'
                      }`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center space-x-2 mb-2">
                              {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? (
                                <Crown className="w-6 h-6 text-rose-500" />
                              ) : (
                                <User className="w-6 h-6 text-gray-500" />
                              )}
                              <h4 className="text-2xl font-bold text-gray-900">
                                {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? 'Growth Plan' : 'Reflect Plan'}
                              </h4>
                            </div>
                            <p className="text-sm text-gray-600">
                              {subscriptionData.trial?.is_trial
                                ? `${subscriptionData.trial.days_remaining} days remaining in your Growth trial`
                                : (subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial')
                                  ? 'Premium features unlocked' 
                                  : 'Free plan with basic features'}
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            (subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial')
                              ? 'bg-rose-100 text-rose-700'
                              : 'bg-gray-200 text-gray-700'
                          }`}>
                            {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? 'Premium' : 'Free'}
                          </div>
                        </div>

                        {/* Trial Information */}
                        {subscriptionData.trial?.is_trial && (
                          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
                            <div className="flex items-center">
                              <Calendar className="w-5 h-5 text-yellow-700 mr-2" />
                              <div>
                                <p className="font-semibold text-yellow-800">Free Trial Active</p>
                                <p className="text-sm text-yellow-700">
                                  {subscriptionData.trial.days_remaining} days remaining of your 7-day Growth trial
                                </p>
                                {subscriptionData.trial.trial_end_date && (
                                  <p className="text-xs text-yellow-600 mt-1">
                                    Trial ends: {new Date(subscriptionData.trial.trial_end_date).toLocaleDateString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pricing Info */}
                        <div className="mb-4">
                          <p className="text-3xl font-bold text-gray-900">
                            {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? '$17' : '$0'}
                            <span className="text-lg font-normal text-gray-600">/month</span>
                          </p>
                        </div>

                        {/* Features List */}
                        <div className="space-y-2 mb-6">
                          {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? (
                            <>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                <span className="font-medium">Unlimited</span>&nbsp;Companion (Conchi) messages
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                <span className="font-medium">Unlimited</span>&nbsp;Divine Guidance sessions
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                <span className="font-medium">Unlimited</span>&nbsp;AI Journaling Insights
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                <span className="font-medium">Unlimited</span>&nbsp;Love Vault saves
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                <span className="font-medium">Unlimited</span>&nbsp;Vision Boards
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Full Love Garden (All 50 levels)
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Full Community access
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Priority support
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  <span className="mr-2 text-gray-400">✓</span>
                                  Companion messages
                                </span>
                                <span className="font-semibold text-rose-600">5/day</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  <span className="mr-2 text-gray-400">✓</span>
                                  Divine Guidance sessions
                                </span>
                                <span className="font-semibold text-rose-600">1/week</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  <span className="mr-2 text-gray-400">✓</span>
                                  AI Journaling Insights
                                </span>
                                <span className="font-semibold text-rose-600">1/week</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  <span className="mr-2 text-gray-400">✓</span>
                                  Love Vault saves
                                </span>
                                <span className="font-semibold text-rose-600">10/month</span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  <span className="mr-2 text-gray-400">✓</span>
                                  Vision Boards
                                </span>
                                <span className="font-semibold text-rose-600">1 board</span>
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Full Love Garden (All 50 levels)
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Full Community access
                              </div>
                              <div className="flex items-center text-sm text-gray-700">
                                <span className="mr-2 text-green-500">✓</span>
                                Daily affirmations
                              </div>
                            </>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                          {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? (
                            <Button
                              onClick={handleManageSubscription}
                              disabled={isManagingSubscription}
                              className="w-full bg-rose-500 hover:bg-rose-600 text-white"
                            >
                              {isManagingSubscription ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Opening Portal...
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Manage Subscription
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              onClick={handleUpgrade}
                              className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
                            >
                              <Crown className="w-4 h-4 mr-2" />
                              Upgrade to Growth Plan
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Billing Information */}
                      {(subscriptionData.plan === 'growth') && !subscriptionData.trial?.is_trial && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="font-semibold text-gray-800 mb-4">Billing Information</h4>
                          <div className="space-y-3 text-sm">
                            {subscriptionData.next_billing_date && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Next billing date:</span>
                                <span className="font-medium text-gray-900">
                                  {new Date(subscriptionData.next_billing_date).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-medium text-gray-900">$17.00 / month</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Payment method:</span>
                              <span className="font-medium text-gray-900">Manage in portal</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Help Text */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            {(subscriptionData.plan === 'growth' || subscriptionData.plan === 'growth_trial') ? (
                              <>
                                <p className="font-semibold mb-1">Manage Your Subscription</p>
                                <p>
                                  Click "Manage Subscription" to update payment methods, view billing history, 
                                  or cancel your subscription. You'll be redirected to our secure billing portal.
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="font-semibold mb-1">Upgrade to Unlock Premium Features</p>
                                <p>
                                  Get unlimited access to Conchi AI, all 50 Love Garden levels, unlimited vision boards, 
                                  and more. Start with a 7-day free trial!
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'security' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-6">Security Settings</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-4">Change Password</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                          <input
                            type="password"
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                            placeholder="Enter current password"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                          <input
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                            placeholder="Enter new password"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                          <input
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                            placeholder="Confirm new password"
                          />
                        </div>

                        <Button onClick={handlePasswordChange} className="bg-rose-500 hover:bg-rose-600">
                          Update Password
                        </Button>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-medium text-gray-700 mb-4">Support & Help</h4>
                      <div className="space-y-3 mb-6">
                        <Link to="/help-center" state={{ from: '/app/profile' }}>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-gray-700 border-gray-300 hover:bg-gray-50"
                          >
                            <HelpCircle className="w-4 h-4 mr-2" />
                            Help Center
                          </Button>
                        </Link>
                        <Link to="/live-support">
                          <Button
                            variant="outline"
                            className="w-full justify-start text-gray-700 border-gray-300 hover:bg-gray-50"
                          >
                            <AlertCircle className="w-4 h-4 mr-2" />
                            Live Support Chat
                          </Button>
                        </Link>
                      </div>

                      <h4 className="font-medium text-gray-700 mb-4">Account Actions</h4>
                      <Button
                        onClick={handleLogout}
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-6">Notification Preferences</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Daily Affirmations</h4>
                        <p className="text-sm text-gray-600">Receive your daily affirmation reminder</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Astrology Updates</h4>
                        <p className="text-sm text-gray-600">Get notified about your daily horoscope</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Referral Updates</h4>
                        <p className="text-sm text-gray-600">Updates about your referral program progress</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Community Activity</h4>
                        <p className="text-sm text-gray-600">Notifications about community posts and replies</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Shop & Offers</h4>
                        <p className="text-sm text-gray-600">New products and special discount offers</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'privacy' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-6">Privacy Settings</h3>
                  
                  {/* What Conchi Remembers Section */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-800">🤖 What Conchi Remembers</h4>
                      <Button
                        onClick={loadConchiMemory}
                        size="sm"
                        variant="outline"
                        disabled={privacyLoading}
                        className="border-rose-300 text-rose-600 hover:bg-rose-50"
                      >
                        {privacyLoading ? 'Loading...' : 'Refresh'}
                      </Button>
                    </div>
                    
                    {privacyLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-500 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading Conchi's memory...</p>
                      </div>
                    ) : conchiMemory ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <h5 className="font-medium text-blue-900 mb-2">Conversations</h5>
                            <p className="text-sm text-blue-800">{conchiMemory.total_conversations || 0} conversations</p>
                            <p className="text-xs text-blue-600">{conchiMemory.total_messages || 0} total messages</p>
                          </div>
                          <div>
                            <h5 className="font-medium text-blue-900 mb-2">Personal Data</h5>
                            <p className="text-sm text-blue-800">{conchiMemory.journal_entries || 0} journal entries</p>
                            <p className="text-xs text-blue-600">{conchiMemory.mood_checkins || 0} mood check-ins</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Button
                            onClick={exportUserData}
                            size="sm"
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            📄 Export All Data
                          </Button>
                          <Button
                            onClick={deleteConchiMemory}
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            🧠 Clear Conchi's Memory
                          </Button>
                        </div>
                        
                        <p className="text-xs text-blue-600">
                          Conchi uses this data to provide personalized support. You can view, export, or delete this data anytime.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-gray-600">No data loaded yet</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Profile Visibility</h4>
                        <p className="text-sm text-gray-600">Allow other users to see your profile in the community</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Activity Status</h4>
                        <p className="text-sm text-gray-600">Show when you were last active</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-700">Data Analytics</h4>
                        <p className="text-sm text-gray-600">Help improve our app by sharing anonymous usage data</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5 text-rose-500" defaultChecked />
                    </div>

                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-medium text-gray-700 mb-4 text-red-600">Account Management</h4>
                      <div className="space-y-3">
                        <Button 
                          onClick={exportUserData}
                          variant="outline" 
                          className="text-blue-600 border-blue-600 hover:bg-blue-50 w-full sm:w-auto"
                        >
                          📄 Export My Complete Data
                        </Button>
                        <Button 
                          onClick={() => {
                            if (confirm('Are you sure you want to permanently delete your account? This action cannot be undone.')) {
                              toast({
                                title: "Account Deletion",
                                description: "Please contact support@constantloveco.com to delete your account.",
                                duration: 6000,
                              });
                            }
                          }}
                          variant="outline" 
                          className="text-red-600 border-red-600 hover:bg-red-50 w-full sm:w-auto"
                        >
                          🗑️ Delete Account
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default Profile;